// src/components/AttendanceCard.jsx
import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./AttendanceCard.css";
import SideGraph from "./SideGraph";

function AttendanceCard({ employee }) {
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  const formatDMY = (d) =>
    new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });

  const asDMY = (v) => {
    if (!v) return "-";
    const s = String(v).trim();
    if (/^\d{2}\s\w{3}\s\d{4}$/.test(s)) return s;
    return formatDMY(s);
  };

  const formatIST = (date) =>
    new Date(date).toLocaleTimeString("en-IN", {
      hour12: true,
      timeZone: "Asia/Kolkata",
    });

  const parseIST12hToToday = (str) => {
    const d = new Date();
    const [time, meridianRaw] = String(str).split(" ");
    const [h, m, s] = time.split(":");
    let hour = parseInt(h, 10);
    const meridian = (meridianRaw || "").toLowerCase();
    if (meridian === "pm" && hour !== 12) hour += 12;
    if (meridian === "am" && hour === 12) hour = 0;
    d.setHours(hour, parseInt(m || "0", 10), parseInt(s || "0", 10), 0);
    return d;
  };

  const calculateDuration = (inTime, outTime) => {
    if (inTime && outTime) {
      const d1 = parseIST12hToToday(inTime);
      const d2 = parseIST12hToToday(outTime);
      const diffMs = Math.max(0, d2 - d1);
      const hours = Math.floor(diffMs / 3_600_000);
      const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
      return `${hours}h ${minutes}m`;
    }
    return "-";
  };

  const now = new Date();
  const todayStr = formatDMY(now);
  const altTodayStr = now.toDateString();
  const monthStr = now.toLocaleString("en-GB", { month: "short", timeZone: "Asia/Kolkata" });
  const dayName = now.toLocaleDateString("en-GB", { weekday: "long", timeZone: "Asia/Kolkata" });
  const isSunday = dayName === "Sunday";
  const employeeName = employee?.name || "";

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    async function fetchRecords() {
      if (!employeeName) return;
      try {
        const res = await fetch(
          `https://hrms.urbango.co.in/server/index.php?endpoint=attendance&employeeName=${encodeURIComponent(
            employeeName
          )}`
        );
        const records = await res.json();
        const arr = Array.isArray(records) ? records : [];
        setAttendanceRecords(arr);

        const todayRecord =
          arr.find((r) => String(r.date).trim() === todayStr) ||
          arr.find((r) => String(r.date).trim() === altTodayStr);

        if (!todayRecord) {
          setCheckInTime(null);
          setCheckOutTime(null);
        } else {
          setCheckInTime(todayRecord.checkIn || null);
          setCheckOutTime(todayRecord.checkOut || null);
        }
      } catch (e) {
        console.error("Fetch attendance failed:", e);
        setAttendanceRecords([]);
        setCheckInTime(null);
        setCheckOutTime(null);
      }
    }
    fetchRecords();
  }, [employeeName, todayStr, altTodayStr]);

  const handleCheckIn = async () => {
    if (!checkInTime && !isSunday && employeeName) {
      const formattedCheckIn = formatIST(new Date());
      setCheckInTime(formattedCheckIn);
      try {
        await fetch("https://hrms.urbango.co.in/server/index.php?endpoint=attendance", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            date: todayStr,
            month: monthStr,
            status: "Present",
            checkIn: formattedCheckIn,
            employeeName,
          }),
        });
        const res = await fetch(
          `https://hrms.urbango.co.in/server/index.php?endpoint=attendance&employeeName=${encodeURIComponent(
            employeeName
          )}`
        );
        const records = await res.json();
        setAttendanceRecords(Array.isArray(records) ? records : []);
      } catch (err) {
        console.error("❌ Error check-in:", err);
      }
    }
  };

  const handleCheckOut = async () => {
    if (checkInTime && !checkOutTime && !isSunday && employeeName) {
      const formattedCheckOut = formatIST(new Date());
      const duration = calculateDuration(checkInTime, formattedCheckOut);
      try {
        await fetch("https://hrms.urbango.co.in/server/index.php?endpoint=attendance", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            date: todayStr,
            month: monthStr,
            status: "Present",
            checkIn: checkInTime,
            checkOut: formattedCheckOut,
            duration,
            employeeName,
          }),
        });
        const res = await fetch(
          `https://hrms.urbango.co.in/server/index.php?endpoint=attendance&employeeName=${encodeURIComponent(
            employeeName
          )}`
        );
        const records = await res.json();
        const arr = Array.isArray(records) ? records : [];
        setAttendanceRecords(arr);
        const todayRecord =
          arr.find((r) => String(r.date).trim() === todayStr) ||
          arr.find((r) => String(r.date).trim() === altTodayStr);
        setCheckOutTime(todayRecord?.checkOut || formattedCheckOut);
      } catch (err) {
        console.error("❌ Error check-out:", err);
      }
    }
  };

  const disableCheckIn = !!checkInTime || isSunday;
  const disableCheckOut = !checkInTime || !!checkOutTime || isSunday;

  return (
    <div className="attendance-root">
      {/* subtle background zoom layer */}
      <div className="attn-bg" aria-hidden="true" />

      <div className="attendance-layout container-fluid">
        <div className="attendance-main">
          <div className="attendance-card">
            <h2 className="attendant">Attendance for {todayStr}</h2>

            <div className="buttons-column">
              <button
                onClick={handleCheckIn}
                disabled={disableCheckIn}
                className="checkin-btn"
              >
                Check-In <br />
                {checkInTime ? checkInTime : formatIST(currentTime)}
              </button>

              <button
                onClick={handleCheckOut}
                disabled={disableCheckOut}
                className="checkout-btn"
              >
                Check-Out <br />
                {checkOutTime ? checkOutTime : formatIST(currentTime)}
              </button>
            </div>

            <div className="calendar-section">
              <Calendar className="calendar" />
            </div>

            <div className="attendance-record">
              <h2 className="attendant">My Attendance Records</h2>
              <div className="scroll-table-wrapper">
                <table className="attendance-table">
                  <thead className="headattendance">
                    <tr className="headattendance">
                      <th>Date</th>
                      <th>Month</th>
                      <th>Status</th>
                      <th>Check-In</th>
                      <th>Check-Out</th>
                      <th>Duration</th>
                      <th>Employee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceRecords.length === 0 ? (
                      <tr>
                        <td colSpan="7">No records yet</td>
                      </tr>
                    ) : (
                      attendanceRecords.map((rec, idx) => (
                        <tr key={idx}>
                          <td>{asDMY(rec.date)}</td>
                          <td>{rec.month || "-"}</td>
                          <td>{rec.status || "-"}</td>
                          <td>{rec.checkIn || "-"}</td>
                          <td>{rec.checkOut || "-"}</td>
                          <td>{rec.duration || "-"}</td>
                          <td>{rec.employeeName || employeeName}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <aside className="attendance-side">
          <SideGraph attendanceRecords={attendanceRecords} employeeName={employeeName} />
        </aside>
      </div>
    </div>
  );
}

export default AttendanceCard;
